import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { MenuIcon } from '@/components/MenuIcon';
import {
  GAP,
  LAYOUT_COLUMNS,
  resolveLayout,
  ROW_UNIT,
  snapColumn,
  snapHeight,
  snapWidth,
  WIDTH_MIN,
  type PlacedWidget,
  type ResolvedBox,
} from '@/pages/home/widgetLayout';
import { WidgetBoundary } from '@/pages/home/widgets/WidgetFrame';

/**
 * 좌표 격자 — 「배치 커스텀」이 만든 배치를 그린다.
 *
 * 한 줄을 100칸으로 쪼개고, 위젯은 자기 좌표(열·행)에 자기 배율(%)만큼 자리를 잡는다. 화면이
 * 좁아져도 <b>한 줄에 몇 개가 보이는지가 변하지 않고</b> 위젯이 같이 줄어든다. 자리 계산과 겹침
 * 풀이는 {@link widgetLayout} 에 있고, 여기서는 그리고·끌고·재는 일만 한다.
 *
 * 칸 사이 간격을 `gap` 이 아니라 <b>칸 안쪽 여백</b>으로 만든 이유도 거기 적었다 — 99개의 간격이
 * 폭을 다 먹으면 1%가 1%가 아니게 된다.
 *
 * ## 높이를 여기서 모으는 이유
 *
 * 세로 자리는 「내용 높이의 몇 %」 라 <b>재 봐야 안다</b>. 그런데 겹침을 푸는 일은 위젯 하나가
 * 아니라 <b>전부</b>를 놓고 봐야 하므로, 칸마다 잰 키를 여기로 올려 모은다. 올라온 값이 같으면
 * 상태를 바꾸지 않으므로(`report`) 재고-그리고-다시-재는 고리가 한 바퀴에 멎는다.
 */

/** 편집 중일 때만 넘어오는 손잡이. 없으면 보기 전용이다. */
export type LayoutEditing = {
  onResize: (widgetKey: string, widthPercent: number, heightPercent: number) => void;
  onTogglePin: (widgetKey: string) => void;
  /** 끌어 옮긴 자리. <b>좌표</b>를 그대로 적는다 — 순서가 아니다. */
  onMove: (widgetKey: string, col: number, row: number) => void;
  /**
   * 겹침까지 풀린 <b>최종</b> 자리를 편집기에 돌려준다.
   *
   * 좌표가 없던 위젯(옛 배치·새로 켠 것)은 {@link resolveLayout} 이 빈 자리를 찾아 앉히는데,
   * 그 자리는 위젯의 키를 재 봐야 나오므로 편집기가 미리 알 수 없다. 저장할 때 화면에 보이던
   * 그대로가 저장되려면 <b>여기서 받은 값</b>을 써야 한다.
   */
  onResolve: (seats: Array<{ widgetKey: string; col: number; row: number }>) => void;
};

export function ScaledGrid({ items, editing }: { items: PlacedWidget[]; editing?: LayoutEditing }) {
  const gridRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<Record<string, number>>({});
  const [heldKey, setHeldKey] = useState<string | null>(null);

  const report = useCallback((widgetKey: string, height: number) => {
    setNatural((prev) => (prev[widgetKey] === height ? prev : { ...prev, [widgetKey]: height }));
  }, []);

  const boxes = useMemo(() => resolveLayout(items, natural, heldKey), [items, natural, heldKey]);
  useReportResolved(boxes, editing?.onResolve);
  const beginDrag = useCellDrag(editing, gridRef, boxes, setHeldKey);

  return (
    <div
      ref={gridRef}
      className="wgrid"
      style={{ gridTemplateColumns: `repeat(${LAYOUT_COLUMNS}, minmax(0, 1fr))`, gridAutoRows: `${ROW_UNIT}px` }}
    >
      {boxes.map((box) => (
        <ScaledCell
          key={box.item.definition.widgetKey}
          box={box}
          editing={editing}
          gridRef={gridRef}
          dragging={heldKey === box.item.definition.widgetKey}
          onMeasure={report}
          onBeginDrag={beginDrag}
        />
      ))}
    </div>
  );
}

/**
 * 풀린 자리를 편집기에 올려보낸다 — <b>달라졌을 때만</b>.
 *
 * `boxes` 는 그릴 때마다 새 배열이라 그대로 의존성에 걸면 매 렌더 올려보내고, 받는 쪽이 상태를
 * 바꾸면 끝나지 않는다. 자리만 뽑아 문자열로 견줘 실제로 움직였을 때만 알린다.
 */
function useReportResolved(boxes: ResolvedBox[], onResolve?: LayoutEditing['onResolve']) {
  const previous = useRef('');

  useEffect(() => {
    if (!onResolve) return;

    const seats = boxes.map((box) => ({ widgetKey: box.item.definition.widgetKey, col: box.col, row: box.row }));
    const signature = JSON.stringify(seats);
    if (signature === previous.current) return;

    previous.current = signature;
    onResolve(seats);
  }, [boxes, onResolve]);
}

/**
 * 위젯을 <b>끌어서 좌표를 옮긴다</b> — 끄는 동안 배치가 그대로 따라 움직인다.
 *
 * ## 왜 HTML5 드래그를 버렸나
 *
 * 처음에는 `draggable` 로 만들고 놓일 자리에 빨간 선을 그었다. 둘 다 틀렸다(사용자 지시 2026-09-16).
 *
 *  1. <b>결과가 안 보인다.</b> 선 하나로는 「이 둘이 자리를 바꾼다」 정도로만 읽힌다.
 *  2. <b>태블릿에서 아예 안 된다.</b> HTML5 드래그는 터치 이벤트를 받지 못한다. 현장은 패드로 쓴다.
 *
 * 그래서 포인터 이벤트로 바꾸고, 미리보기를 <b>따로 그리지 않는다</b> — 끄는 동안 진짜 좌표를
 * 그때그때 바꾼다. 카드는 손끝을 따라오고 비킬 위젯이 비키므로, 놓기 전에 보이는 그림이 곧 결과다.
 * 취소하면 통째로 되돌아가니(편집 화면의 [취소]) 끌어 보다 마는 것도 공짜다.
 */
function useCellDrag(
  editing: LayoutEditing | undefined,
  gridRef: React.RefObject<HTMLDivElement | null>,
  boxes: ResolvedBox[],
  setHeldKey: (key: string | null) => void,
) {
  // 끄는 동안 바뀌는 값을 손잡이가 늘 최신으로 보게 한다. 열어 둔 클로저에 옛 배열이 잡히면
  // 폭을 바꾼 직후의 첫 드래그가 옛 폭으로 계산된다.
  const latest = useRef(boxes);
  latest.current = boxes;

  return (widgetKey: string, event: React.PointerEvent<HTMLElement>) => {
    if (!editing) return;

    const origin = event.target as HTMLElement;
    const cell = origin.closest<HTMLElement>('.wgrid-cell');
    const hold = cell?.querySelector<HTMLElement>('.wgrid-hold');
    const grid = gridRef.current;
    if (!cell || !hold || !grid) return;
    // 핀·크기 조절 단추 위에서 시작한 것은 끌기가 아니다. 다만 끌기 손잡이는 예외다.
    if (origin.closest('button') && !origin.closest('.wgrid-drag')) return;

    const startRect = hold.getBoundingClientRect();
    const grabX = event.clientX - startRect.left;
    const grabY = event.clientY - startRect.top;
    const startX = event.clientX;
    const startY = event.clientY;

    let moving = false;
    let tx = 0;
    let ty = 0;
    let lastCol = -1;
    let lastRow = -1;

    const move = (moved: PointerEvent) => {
      // 손가락이 조금 떨린 것과 끌기를 가른다. 이 문턱이 없으면 핀을 누르려다 자리가 바뀐다.
      if (!moving) {
        if (Math.abs(moved.clientX - startX) + Math.abs(moved.clientY - startY) < 6) return;
        moving = true;
        setHeldKey(widgetKey);
      }

      /*
        카드를 손끝에 붙여 둔다. 자리를 옮기는 순간 카드의 <b>제자리</b>도 함께 움직이므로,
        옮긴 거리를 누적하면 카드가 손에서 점점 미끄러진다 — 매번 지금 위치를 다시 재서 구한다.
      */
      const rect = hold.getBoundingClientRect();
      tx += moved.clientX - (rect.left + grabX);
      ty += moved.clientY - (rect.top + grabY);
      hold.style.transform = `translate(${Math.round(tx)}px, ${Math.round(ty)}px)`;

      /*
        새 좌표는 카드의 <b>왼쪽 위 모서리</b>가 가리키는 칸이다. 손끝이 가리키는 칸에 앉히면
        카드 가운데를 잡고 끌 때 카드가 손 아래에서 훌쩍 튄다.
      */
      const gridRect = grid.getBoundingClientRect();
      const unit = gridRect.width / LAYOUT_COLUMNS;
      const box = latest.current.find((entry) => entry.item.definition.widgetKey === widgetKey);
      if (!box || unit <= 0) return;

      const col = snapColumn((moved.clientX - grabX - gridRect.left) / unit, box.w);
      const row = Math.max(0, Math.round((moved.clientY - grabY - gridRect.top) / ROW_UNIT));
      if (col === lastCol && row === lastRow) return;

      lastCol = col;
      lastRow = row;
      editing.onMove(widgetKey, col, row);
    };

    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      hold.style.transform = '';
      setHeldKey(null);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    // 터치로 화면을 굴리기 시작하면 브라우저가 포인터를 가져간다. 그때 카드를 제자리에 돌려놔야 한다.
    window.addEventListener('pointercancel', stop);
  };
}

/**
 * 좌표 칸 하나.
 *
 * <h3>높이를 어떻게 재는가</h3>
 * 카드(`section.card`)에는 <b>어떤 높이 제약도 걸지 않는다</b> — 자리를 채우는 늘림은 `min-height`
 * 로만 걸린다. 그래서 잴 때 그 늘림만 잠깐 풀면 언제나 「내용 그대로의 높이」가 나온다. 제약과
 * 측정 대상을 갈라 두지 않으면, 자리를 좁히는 순간 잰 값이 그 좁힌 값을 따라간다.
 */
function ScaledCell({
  box,
  editing,
  gridRef,
  dragging,
  onMeasure,
  onBeginDrag,
}: {
  box: ResolvedBox;
  editing?: LayoutEditing;
  gridRef: React.RefObject<HTMLDivElement | null>;
  dragging: boolean;
  onMeasure: (widgetKey: string, height: number) => void;
  onBeginDrag: (widgetKey: string, event: React.PointerEvent<HTMLElement>) => void;
}) {
  const holdRef = useRef<HTMLDivElement>(null);
  const item = box.item;
  const widgetKey = item.definition.widgetKey;
  const widthPercent = item.widthPercent ?? LAYOUT_COLUMNS;
  const heightPercent = item.heightPercent ?? 100;

  // 렌더마다·크기가 바뀔 때마다 다시 잰다. 같은 값이면 위에서 상태를 안 바꾸므로 고리가 멎는다.
  useLayoutEffect(() => {
    const hold = holdRef.current;
    if (!hold) return;

    const measure = () => {
      const card = hold.firstElementChild;
      if (!(card instanceof HTMLElement)) {
        onMeasure(widgetKey, hold.scrollHeight);
        return;
      }

      /*
        카드는 <b>자리를 꽉 채우도록 늘려</b> 두었다(`min-height: 100%`). 그 상태로 재면 내용이
        아니라 「자리」 를 재는 셈이라, 잰 값이 자기 자신을 먹고 자란다 — 150%로 키운 위젯이
        한 프레임마다 1.5배씩 늘어난다. 그래서 <b>재는 순간에만 늘림을 푼다</b>.

        ★ 같은 작업 안에서 되돌려 놓는다. 화면에 그려지기 전이라 깜빡이지 않고, ResizeObserver 도
          「바뀌었다 돌아온」 크기는 알리지 않는다.
      */
      const stretched = card.style.minHeight;
      card.style.minHeight = '0';
      const height = card.getBoundingClientRect().height;
      card.style.minHeight = stretched;
      onMeasure(widgetKey, height);
    };
    measure();

    window.addEventListener('resize', measure);
    // 폰트는 레이아웃이 끝난 뒤에 바뀔 수 있다. 그때 다시 재지 않으면 첫 화면만 어긋나 보인다.
    document.fonts?.ready.then(measure).catch(() => {});

    // 테스트 환경(jsdom)에는 ResizeObserver 가 없다 — 없으면 위의 두 계기로만 잰다.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer && hold.firstElementChild) observer.observe(hold.firstElementChild);

    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  });

  /**
   * 모서리를 끌어 배율을 바꾼다.
   *
   * 화면 상태를 바로 갱신하되(끌면서 보여야 고를 수 있다) <b>눈금이 바뀔 때만</b> 올려보낸다 —
   * 픽셀마다 올리면 홈의 위젯 전부가 1초에 수십 번 다시 그려진다.
   */
  const startResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!editing) return;
    event.preventDefault();
    event.stopPropagation();

    const gridWidth = gridRef.current?.getBoundingClientRect().width ?? 0;
    if (gridWidth <= 0) return;

    // 세로는 「내용 높이」 가 기준이다. 끄는 도중 폭이 바뀌면 내용 높이도 따라 바뀌는데,
    // 그때마다 기준을 갈아치우면 손이 멈춰 있어도 숫자가 흔들린다. 시작할 때 값으로 고정한다.
    const base = Math.max(1, ((box.h * ROW_UNIT - GAP) * 100) / heightPercent);
    const startX = event.clientX;
    const startY = event.clientY;
    let lastWidth = widthPercent;
    let lastHeight = heightPercent;

    const move = (moved: PointerEvent) => {
      /*
        오른쪽 끝을 넘기지 않는다 — 시작 칸이 정해져 있으므로 폭의 천장은 「남은 칸」 이다.

        ★ 다만 <b>최소 폭 아래로는 내리지 않는다</b>. 오른쪽 끝에 붙은 위젯(col 95)은 남은 칸이
          5뿐이라, 그냥 자르면 서버가 막는 값(@Min(10))이 만들어져 <b>저장이 통째로 400 으로
          떨어진다</b> — 화면에서는 「저장했는데 새로고침하면 풀린다」 로 보인다. 폭을 지키고
          시작 칸을 왼쪽으로 당기는 쪽이 맞다(`resolveLayout` 이 `col` 을 다시 물린다).
      */
      const room = Math.max(WIDTH_MIN, LAYOUT_COLUMNS - box.col);
      const nextWidth = Math.min(room, snapWidth(widthPercent + ((moved.clientX - startX) / gridWidth) * 100));
      const nextHeight = snapHeight(heightPercent + ((moved.clientY - startY) / base) * 100);
      if (nextWidth === lastWidth && nextHeight === lastHeight) return;

      lastWidth = nextWidth;
      lastHeight = nextHeight;
      editing.onResize(widgetKey, nextWidth, nextHeight);
    };
    const stop = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
  };

  const className = [
    'wgrid-cell',
    editing ? 'is-editing' : '',
    item.pinned ? 'is-pinned' : '',
    dragging ? 'is-dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={className}
      style={{ gridColumn: `${box.col + 1} / span ${box.w}`, gridRow: `${box.row + 1} / span ${box.h}` }}
      data-widget-key={widgetKey}
      // 마우스는 카드 아무 데나 잡아도 끌린다. 터치는 손잡이로만 끈다 — 카드 전체를 끌 수 있게 두면
      // `touch-action` 을 꺼야 하고, 그러면 편집 중에 화면을 굴릴 수가 없다.
      onPointerDown={(event) => {
        if (event.pointerType === 'touch') return;
        onBeginDrag(widgetKey, event);
      }}
    >
      {/*
        자리보다 내용이 길면 <b>카드 안에서 스크롤</b>된다. 잘라 버리면(overflow:hidden) 사용자는
        위젯이 그만큼밖에 없는 줄 알고, 줄여 둔 것을 잊은 뒤에는 자료가 사라졌다고 읽는다.
      */}
      <div
        ref={holdRef}
        className="wgrid-hold"
        style={{ overflowY: heightPercent < 100 ? 'auto' : undefined, pointerEvents: editing ? 'none' : undefined }}
      >
        <WidgetBoundary title={item.definition.title}>
          <item.definition.Component />
        </WidgetBoundary>
      </div>

      {editing && (
        <>
          <span className="wgrid-tag">
            {widthPercent}% · {heightPercent}%
          </span>

          {/*
            끌기 손잡이. 마우스는 카드 아무 데나 잡아도 끌리지만 <b>터치는 이것으로만</b> 끈다.
            겸사겸사 「이 카드는 옮길 수 있다」 는 표시도 된다 — 편집 중인데 아무 표시가 없으면
            사람은 카드를 끌어 볼 생각을 안 한다.
          */}
          <span className="wgrid-chrome at-start">
            <button
              type="button"
              className="wgrid-btn wgrid-drag"
              aria-label={`${item.definition.title} 자리 옮기기`}
              title="끌어서 자리를 옮깁니다"
              onPointerDown={(event) => onBeginDrag(widgetKey, event)}
            >
              <MenuIcon name="drag" size={13} />
            </button>
          </span>

          <span className="wgrid-chrome at-end">
            <button
              type="button"
              className="wgrid-btn"
              aria-pressed={item.pinned}
              aria-label={`${item.definition.title} 자리 고정`}
              title={
                item.pinned
                  ? '고정되어 있습니다 — 겹쳐도 이 위젯은 비키지 않습니다'
                  : '고정합니다 — 겹치면 다른 위젯이 비켜 갑니다'
              }
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => editing.onTogglePin(widgetKey)}
            >
              <MenuIcon name="pin" size={13} />
            </button>
          </span>

          <button
            type="button"
            className="wgrid-grip"
            aria-label={`${item.definition.title} 크기 조절`}
            onPointerDown={startResize}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
              <path d="M11 4 4 11M11 8l-3 3" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" />
            </svg>
          </button>
        </>
      )}
    </div>
  );
}
